// src/api/lenderProfile.js
import { apiJSON, apiUpload } from "../client";

const DOCTYPE = "Lender Profile";

/**
 * Upload logo image to Frappe and return file_url
 */
async function uploadLogoGetUrl(file, attachTo = null) {
  if (!file) return null;

  // Ensure file has a name
  const fileName = file.name || file.filename || "logo";
  
  const form = new FormData();
  form.append("file", file, fileName);
  form.append("is_private", "0"); // public logo
  
  // ✅ Add required fields for Frappe File doctype (required in production)
  // file_name is mandatory - Frappe needs this to create the File record
  form.append("file_name", fileName);
  
  // ✅ Only append doctype/docname if we're attaching to a specific document
  // Don't append empty docname - it causes "Attached To Name must be a string or an integer" error
  if (attachTo?.doctype && attachTo?.name) {
    form.append("doctype", attachTo.doctype);
    form.append("docname", attachTo.name);
  }
  // For standalone files, don't append doctype/docname at all

  const res = await apiUpload("/api/method/upload_file", form);

  const fileUrl =
    res?.message?.file_url ||
    res?.data?.file_url ||
    res?.file_url ||
    null;

  if (!fileUrl) {
    console.error("❌ Logo upload response:", res);
    throw new Error("Logo upload succeeded but no file_url returned.");
  }
  
  console.log("✅ Logo uploaded successfully:", fileUrl);
  return fileUrl;
}

/**
 * Get lender profile by document name (e.g. "lender@test.com")
 * GET: /api/resource/Lender Profile/{docname}
 */
export async function getLenderProfile(docname) {
  if (!docname || docname.trim() === "") {
    throw new Error("Missing docname for getLenderProfile");
  }

  const endpoint =
    "/api/resource/" +
    encodeURIComponent(DOCTYPE) +
    "/" +
    encodeURIComponent(docname);

  try {
    const res = await apiJSON(endpoint, { method: "GET" });
    console.log("✅ GET Lender Profile response:", res);
    
    // Frappe usually wraps doc as { data: {...} }
    return res?.data || res?.message || res;
  } catch (error) {
    console.error("❌ GET Lender Profile error:", error);
    // Re-throw with more context
    if (error?.response?.status === 404) {
      const notFoundError = new Error(`Lender Profile not found: ${docname}`);
      notFoundError.status = 404;
      throw notFoundError;
    }
    throw error;
  }
}

/**
 * Create lender profile.
 * POST: /api/resource/Lender Profile
 * If docname is passed, force that as the document name.
 */
export async function createLenderProfile(docname, payload) {
  if (!docname || docname.trim() === "") {
    throw new Error("Missing docname for createLenderProfile");
  }

  const endpoint = "/api/resource/" + encodeURIComponent(DOCTYPE);

  const data = { ...(payload || {}) };
  if (docname) {
    data.name = docname;
  }

  try {
    console.log("📤 CREATE Lender Profile payload:", data);
    const res = await apiJSON(endpoint, {
      method: "POST",
      data,
    });
    console.log("✅ CREATE Lender Profile response:", res);
    
    return res?.data || res?.message || res;
  } catch (error) {
    console.error("❌ CREATE Lender Profile error:", error);
    throw error;
  }
}

/**
 * Update lender profile by document name.
 * PUT: /api/resource/Lender Profile/{docname}
 */
export async function updateLenderProfile(docname, payload) {
  if (!docname || docname.trim() === "") {
    throw new Error("Missing docname for updateLenderProfile");
  }

  const endpoint =
    "/api/resource/" +
    encodeURIComponent(DOCTYPE) +
    "/" +
    encodeURIComponent(docname);

  try {
    console.log("📤 UPDATE Lender Profile payload:", payload);
    const res = await apiJSON(endpoint, {
      method: "PUT",
      data: payload || {},
    });
    console.log("✅ UPDATE Lender Profile response:", res);
    
    return res?.data || res?.message || res;
  } catch (error) {
    console.error("❌ UPDATE Lender Profile error:", error);
    throw error;
  }
}

/**
 * High-level save helper:
 *  - optionally uploads logoFile and sets lender_logo/logo/logo_url
 *  - does create or update as needed
 *
 * API Endpoints:
 *   GET:  /api/resource/Lender Profile/{docname}
 *   PUT:  /api/resource/Lender Profile/{docname}
 *   POST: /api/resource/Lender Profile
 *
 * options:
 *   - logoFile?: File
 *   - exists?: boolean  (true = update, false = create, undefined = try update then create on 404)
 */
export async function saveLenderProfile(docname, payload, options = {}) {
  if (!docname || docname.trim() === "") {
    throw new Error("Missing docname for saveLenderProfile");
  }

  const { logoFile, exists } = options || {};
  const data = { ...(payload || {}) };

  // 1) Upload logo if provided
  if (logoFile) {
    try {
      const fileUrl = await uploadLogoGetUrl(logoFile);
      // Map to several possible backend fields - harmless if some don't exist
      data.lender_logo = fileUrl;
      data.logo = fileUrl;
      data.logo_url = fileUrl;
    } catch (logoErr) {
      console.error("❌ Logo upload failed:", logoErr);
      // Continue without logo - don't fail the entire save
    }
  }

  // 2) Decide create vs update
  if (exists === true) {
    return updateLenderProfile(docname, data);
  }
  if (exists === false) {
    return createLenderProfile(docname, data);
  }

  // If we don't know, try update first, fallback to create on 404
  try {
    return await updateLenderProfile(docname, data);
  } catch (err) {
    const msg = String(err?.message || "");
    const status = err?.status || err?.response?.status;
    
    if (status === 404 || msg.includes("404") || msg.toLowerCase().includes("not found")) {
      console.log("ℹ️ Profile not found, creating new profile...");
      return createLenderProfile(docname, data);
    }
    throw err;
  }
}

/** For debugging / reuse if needed */
export const __lenderProfileInternals = {
  uploadLogoGetUrl,
};
